import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const categoriesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoriesRef.current && !categoriesRef.current.contains(e.target as Node)) {
        setCategoriesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-black/45 via-black/20 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 transition-colors group"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`ri-file-list-3-line text-2xl ${scrolled ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <span className={`font-heading font-semibold text-lg whitespace-nowrap ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}>
              Basetao Sheet Hub
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line text-sm"></i>
              </div>
              Home
            </Link>

            <div className="relative" ref={categoriesRef}>
              <button
                onClick={() => setCategoriesOpen(!categoriesOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-sm"></i>
                </div>
                <span>Shopping Guide</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  {categoriesOpen ? (
                    <i className="ri-arrow-up-s-line text-sm"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  )}
                </div>
              </button>

              {categoriesOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/80 py-2 max-h-80 overflow-y-auto">
                  <p className="px-4 py-1.5 text-xs font-medium text-foreground-400 uppercase tracking-wider">Categories</p>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      onClick={() => setCategoriesOpen(false)}
                    >
                      <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Basetao Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-md transition-colors whitespace-nowrap ml-1"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50 hover:bg-white/10'
            }`}
          >
            {mobileOpen ? (
              <i className="ri-close-line text-lg"></i>
            ) : (
              <i className="ri-menu-line text-lg"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-line text-base"></i>
              </div>
              Home
            </Link>

            <div className="mt-1 mb-2 px-3 py-1.5">
              <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider">Shopping Guide — Categories</p>
            </div>

            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-md transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-4 h-4 flex items-center justify-center text-primary-500">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-background-200/70 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-external-link-line text-base"></i>
                </div>
                Basetao Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-background-50 bg-accent-500 rounded-md"
                onClick={() => setMobileOpen(false)}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-base"></i>
                </div>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}